import { compose, lifecycle, withHandlers, withProps, withPropsOnChange, mapProps } from 'recompose';
import PropTypes from 'prop-types';
import { tap, curry, cond, concat, prop, uniq, uniqBy, reduce, complement, isNil, pipe, compose as compose$1, omit, head, split } from 'ramda';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread';
import withStateHandlers from 'recompose/withStateHandlers';
import branch from 'recompose/branch';
import renderNothing from 'recompose/renderNothing';

var NullComponent = function NullComponent() {
  return null;
};

var createContainer = function createContainer() {
  return compose.apply(void 0, arguments)(NullComponent);
};

var onMount = function onMount(handleOnMount) {
  return lifecycle({
    componentDidMount: function componentDidMount() {
      handleOnMount(this.props);
    }
  });
};

var OnDidMount = createContainer(onMount(function (props) {
  return props.handler(props.prop);
}));
OnDidMount.propTypes = {
  handler: PropTypes.func.isRequired,
  prop: PropTypes.any
};
OnDidMount.defaultProps = {
  prop: null
};

var oneOfPropHasChanged = function oneOfPropHasChanged(prevProps, props, keys) {
  return keys.reduce(function (hasChanged, key) {
    return hasChanged || prevProps[key] !== props[key];
  }, false);
};

var onPropsDidUpdate = function onPropsDidUpdate(propNames, handleOnUpdate) {
  return lifecycle({
    componentDidUpdate: function componentDidUpdate(prevProps) {
      if (!propNames || propNames.length <= 0) {
        return prevProps !== this.props && handleOnUpdate(this.props, prevProps);
      }

      return oneOfPropHasChanged(prevProps, this.props, propNames) && handleOnUpdate(this.props, prevProps);
    }
  });
};

var onMountOrUpdate = function onMountOrUpdate(propNamesForUpdate, handler) {
  return compose(onPropsDidUpdate(propNamesForUpdate, handler), onMount(handler));
};

var OnDidMountOrUpdate = createContainer(onMountOrUpdate(['prop'], function (props) {
  return props.handler(props.prop);
}));
OnDidMountOrUpdate.propTypes = {
  prop: PropTypes.any,
  handler: PropTypes.func.isRequired
};
OnDidMountOrUpdate.defaultProps = {
  prop: null
};

var onWillUnmount = function onWillUnmount(handleOnUnmount) {
  return lifecycle({
    componentWillUnmount: function componentWillUnmount() {
      handleOnUnmount(this.props);
    }
  });
};

var loadFromProps = function loadFromProps(setIsLoadingFromProps, errorHandlerFromProps, promiseFromProps) {
  return function (props) {
    return Promise.resolve('FIX').then(tap(function () {
      return setIsLoadingFromProps(props)(true);
    })).then(function () {
      return promiseFromProps(props);
    }).catch(tap(errorHandlerFromProps(props))).then(tap(function () {
      return setIsLoadingFromProps(props)(false);
    }));
  };
};

var withHandler = function withHandler(key, handler) {
  return withHandlers(_defineProperty({}, key, handler));
};

var withLoadingHandler = function withLoadingHandler(key, setIsLoadingFromProps, errorHandlerFromProps, promiseFromProps) {
  return withHandler(key, function (props) {
    return function () {
      return loadFromProps(setIsLoadingFromProps, errorHandlerFromProps, promiseFromProps)(props);
    };
  });
};

// handle axios errors, prototypisch:
var handleAxiosError = function handleAxiosError(error) {
  if (error.response) ; else if (error.request) ;
};

var checkGlobalVariable = function checkGlobalVariable(variableName) {
  return new Promise(function (resolve) {
    var check = function check() {
      return window[variableName] ? resolve(window[variableName]) : setTimeout(check, 200);
    };

    check();
  });
};

var isStatusCode = function isStatusCode(statusCode) {
  return function (error) {
    return error && error.response && error.response.status === statusCode;
  };
};

var is404 = isStatusCode(404);
var is408 = isStatusCode(408);

var axiosAborted = function axiosAborted(error) {
  return error && error.code === 'ECONNABORTED';
};

var connectionAborted = function connectionAborted(error) {
  return is408(error) || axiosAborted(error);
};
var isError = function isError(error) {
  return error && error.stack && error.message && typeof error.stack === 'string' && typeof error.message === 'string';
};

var convertToError = function convertToError(obj) {
  if (!obj) {
    return new Error();
  }

  if (typeof obj === 'string') {
    return new Error(obj);
  }

  return new Error(JSON.stringify(obj, null, 2));
};

var toError = function toError(error) {
  return isError(error) ? error : convertToError(error);
};

var mailRegEx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/; // eslint-disable-line no-useless-escape

var formikEmailValidator = function formikEmailValidator(message) {
  return function (value) {
    return !mailRegEx.test(value) && message;
  };
};

var formikNotEmptyValidator = function formikNotEmptyValidator(message) {
  return function (value) {
    return !value && message;
  };
};

var germanComponentRestrictions = {
  country: 'de'
}; // see https://developers.google.com/maps/documentation/javascript/reference#AutocompletionRequest

var AUTO_COMPLETE_REQUEST_OPTIONS = {
  types: ['geocode'],
  // geocode for addresses
  componentRestrictions: germanComponentRestrictions
};
var promiseGeocode = function promiseGeocode(geocoderOkStatus, geocoder, params) {
  return new Promise(function (resolve, reject) {
    return geocoder.geocode(params, function (results, status) {
      return status === geocoderOkStatus ? resolve(results) : reject(status);
    });
  });
}; // https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingRequests

var geocodeGermanAddressWithGoogleGeocoder = curry(function (geocoderOkStatus, geocoder, address) {
  return promiseGeocode(geocoderOkStatus, geocoder, {
    address: address,
    componentRestrictions: germanComponentRestrictions
  });
});

var base64ToBlob = function base64ToBlob(image) {
  var byteString = atob(image.split(',')[1]); // separate out the mime component

  var mimeString = image.split(',')[0].split(':')[1].split(';')[0]; // write the bytes of the string to a typed array

  var ia = new Uint8Array(byteString.length); // eslint-disable-next-line no-plusplus

  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], {
    type: mimeString
  });
};

var fileToDataUri = function fileToDataUri(file) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();

    reader.onloadend = function () {
      return resolve(reader.result);
    };

    reader.onerror = function () {
      return reject(reader.error);
    };

    reader.readAsDataURL(file);
  });
};

var testPlatformForIos = function testPlatformForIos(platform) {
  return /iPad|iPhone|iPod/.test(platform);
};

var testUserAgentForIos = function testUserAgentForIos(userAgent) {
  return /iPad|iPhone|iPod/.test(userAgent);
};

var checkBrowserForIos = cond([[function () {
  return navigator.platform;
}, function () {
  return testPlatformForIos(navigator.platform);
}], [function () {
  return navigator.userAgent;
}, function () {
  return testUserAgentForIos(navigator.userAgent);
}], [function () {
  return true;
}, function () {
  return false;
}]]);
var isIos = checkBrowserForIos();

var uniqueArrayConcat = function uniqueArrayConcat() {
  var map = {};
  var res = [];

  for (var _len = arguments.length, arrays = new Array(_len), _key = 0; _key < _len; _key++) {
    arrays[_key] = arguments[_key];
  }

  arrays.forEach(function (arr) {
    return arr.forEach(function (item) {
      if (!map[item]) {
        map[item] = true;
        res.push(item);
      }
    });
  });
  return res;
};
var headIfExists = function headIfExists(arr) {
  return arr && arr.length > 0 && arr[0];
};
var mergeTwoArraysUniq = function mergeTwoArraysUniq(array1, array2) {
  return uniq(concat(array1, array2));
};
var mergeTwoArraysUniqForProp = function mergeTwoArraysUniqForProp(propName, array1, array2) {
  return uniqBy(prop(propName), concat(array1, array2));
};

var isAny = function isAny() {
  for (var _len = arguments.length, booleans = new Array(_len), _key = 0; _key < _len; _key++) {
    booleans[_key] = arguments[_key];
  }

  return booleans.reduce(function (isTrue, current) {
    return isTrue || current;
  }, false);
};

var getErrorFromProp = function getErrorFromProp(defaultText, error) {
  if (!error) {
    return new Error(defaultText);
  }

  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (Array.isArray(error) || _typeof(error) === 'object') {
    return new Error(JSON.stringify(error));
  }

  return new Error(defaultText);
};

var isFunction = function isFunction(dubious) {
  return typeof dubious === 'function';
};
var getPrevMemoizedFunc = function getPrevMemoizedFunc(func) {
  var prevVal;
  var validationResult = false;
  return function (val) {
    if (prevVal !== val) {
      validationResult = func(val);
    }

    return validationResult;
  };
};

// polyfill from https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Number/isNaN
// eslint-disable-next-line no-self-compare
var polyfilledIsNan = function polyfilledIsNan(val) {
  return val !== val;
};

var pIsNaN = Number.isNaN || polyfilledIsNan;

var parseFloatOrDefault = function parseFloatOrDefault(numb) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? null : _ref$defaultValue;

  if (numb === undefined || numb === null) {
    return defaultValue;
  }

  var res = parseFloat(numb.toString());
  return pIsNaN(res) ? defaultValue : res;
};
var parseIntOrDefault = function parseIntOrDefault(intVal) {
  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref2$defaultValue = _ref2.defaultValue,
      defaultValue = _ref2$defaultValue === void 0 ? null : _ref2$defaultValue;

  if (intVal === undefined || intVal === null) {
    return defaultValue;
  }

  var res = parseInt(intVal.toString(), 10);
  return pIsNaN(res) ? defaultValue : res;
};

var square = function square(num) {
  return num * num;
};

var deg2rad = function deg2rad(deg) {
  return deg * (Math.PI / 180);
};

var computeDistanceOnEarth = function computeDistanceOnEarth(_ref3, _ref4) {
  var latitude = _ref3.latitude,
      longitude = _ref3.longitude;
  var latitude2 = _ref4.latitude,
      longitude2 = _ref4.longitude;
  var earthRadius = 6371; // in km

  var dLat = deg2rad(latitude2 - latitude);
  var dLon = deg2rad(longitude2 - longitude);
  var a = square(Math.sin(dLat / 2)) + Math.cos(deg2rad(latitude)) * Math.cos(deg2rad(latitude2)) * square(Math.sin(dLon / 2));
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c; // Distance in km
};

var tryParseJson = function tryParseJson(jsonString) {
  try {
    return jsonString === undefined ? jsonString : JSON.parse(jsonString);
  } catch (ex) {
    console.log(ex);
    return null;
  }
};
var isObject = function isObject(val) {
  return val && _typeof(val) === 'object';
};
var mergeArrayProp = function mergeArrayProp(obj, prop$$1, additional) {
  return _objectSpread({}, obj, _defineProperty({}, prop$$1, _toConsumableArray(obj[prop$$1]).concat([additional])));
};
var objectFromKeys = function objectFromKeys(keys, keyValueHandler) {
  var res = {}; // eslint-disable-next-line no-return-assign

  keys.forEach(function (key) {
    return res[key] = keyValueHandler(key);
  });
  return res;
};
var pick = function pick(props, obj) {
  return objectFromKeys(props, function (key) {
    return obj[key];
  });
};
var removeFalsyProps = function removeFalsyProps(obj) {
  return pick(Object.keys(obj).filter(function (key) {
    return obj[key];
  }), obj);
};
var differentKeys = function differentKeys(keys, a, b) {
  return keys.filter(function (key) {
    return a[key] !== b[key];
  });
};
var duplicateKeys = function duplicateKeys(a, b) {
  return a && b ? Object.keys(a).filter(function (objKey) {
    return Object.prototype.hasOwnProperty.call(b, objKey);
  }) : [];
};
var objectToArray = function objectToArray(obj) {
  return Object.keys(obj).map(function (key) {
    return obj[key];
  });
};
var findInObject = function findInObject(findFn, obj) {
  return objectToArray(obj).filter(findFn)[0];
};

var timeoutPromise = function timeoutPromise(timeoutTime) {
  return new Promise(function (resolve) {
    return setTimeout(resolve, timeoutTime);
  });
};

var promiseWithError = function promiseWithError(testOutput) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$throwsException = _ref.throwsException,
      throwsException = _ref$throwsException === void 0 ? false : _ref$throwsException,
      _ref$errorData = _ref.errorData,
      errorData = _ref$errorData === void 0 ? {} : _ref$errorData,
      _ref$timeOut = _ref.timeOut,
      timeOut = _ref$timeOut === void 0 ? 600 : _ref$timeOut;

  return timeoutPromise(timeOut).then(function () {
    return {
      data: testOutput
    };
  }).then(function (data) {
    if (throwsException) {
      var error = new Error('test-error');
      error.data = data; // eslint-disable-next-line no-throw-literal

      throw _objectSpread({}, error, errorData);
    }

    return data;
  });
};

var concatAll = reduce(concat);

var first = function first() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return fns.reduce(function (val, fn) {
    return val || fn();
  }, null);
};

var isNotNil = complement(isNil);

var pipeX = function pipeX() {
  return pipe.apply(void 0, arguments)(null);
};

var propIsNil = function propIsNil(propName) {
  return compose$1(isNil, prop(propName));
};

var propIsNotNil = function propIsNotNil(propName) {
  return compose$1(isNotNil, prop(propName));
};

var pTap = function pTap(func) {
  return function (value) {
    return Promise.resolve().then(function () {
      return func(value);
    }).then(function () {
      return value;
    });
  };
};

var switchF = function switchF() {
  for (var _len = arguments.length, whens = new Array(_len), _key = 0; _key < _len; _key++) {
    whens[_key] = arguments[_key];
  }

  return cond(whens)(null);
};

var getComponentDisplayName = function getComponentDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

var getStateHandlersForPropName = function getStateHandlersForPropName(errorPropName) {
  return {
    setError: function setError() {
      return function (error) {
        return _defineProperty({}, errorPropName, error);
      };
    },
    resetError: function resetError() {
      return function () {
        return _defineProperty({}, errorPropName, null);
      };
    }
  };
};

var withCustomErrorProp = function withCustomErrorProp(errorPropName) {
  return withStateHandlers(_defineProperty({}, errorPropName, null), getStateHandlersForPropName(errorPropName));
};
withCustomErrorProp('error');

var renderIf = function renderIf(prediction) {
  return branch(complement(prediction), renderNothing);
};

var propNameIsNil = function propNameIsNil(propName) {
  return compose$1(isNil, prop(propName));
};

var renderIfHasProp = function renderIfHasProp(propName) {
  return renderIf(complement(propNameIsNil(propName)));
};
var renderIfHasError = renderIfHasProp('error');

var withComputedProp = function withComputedProp(key, handler) {
  return withProps(function (props) {
    return _defineProperty({}, key, handler(props));
  });
};

var withProp = function withProp(key, value) {
  return typeof value === 'function' ? withComputedProp(key, value) : withProps(_defineProperty({}, key, value));
};

var withPropOnChange = function withPropOnChange(shouldMapOrKeys, key, handler) {
  return withPropsOnChange(shouldMapOrKeys, function (props) {
    return _defineProperty({}, key, handler(props));
  });
};

var getEmailValidator = function getEmailValidator() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$errorText = _ref.errorText,
      errorText = _ref$errorText === void 0 ? 'Kein gültiges E-Mail-Format' : _ref$errorText;

  var mailRegEx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/; // eslint-disable-line no-useless-escape

  return function (value) {
    return mailRegEx.test(value) ? undefined : errorText;
  };
};
var emailValidator = getEmailValidator();

var getMinLengthValidator = function getMinLengthValidator() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$minLength = _ref.minLength,
      minLength = _ref$minLength === void 0 ? 1 : _ref$minLength,
      _ref$errorText = _ref.errorText,
      errorText = _ref$errorText === void 0 ? "Die Eingabe muss mindestens ".concat(minLength, " Zeichen lang sein") : _ref$errorText;

  return function (value) {
    return value && (value.length > minLength ? undefined : errorText);
  };
};
getMinLengthValidator();

var getNotEmptyValidator = function getNotEmptyValidator() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$errorEmptyText = _ref.errorEmptyText,
      errorEmptyText = _ref$errorEmptyText === void 0 ? 'Das Feld muss ausgefüllt werden' : _ref$errorEmptyText,
      _ref$errorInvalidText = _ref.errorInvalidText,
      errorInvalidText = _ref$errorInvalidText === void 0 ? 'Überprüfen Sie Ihre Eingabe' : _ref$errorInvalidText;

  return function (value) {
    if (!value) {
      return errorEmptyText;
    }

    if (!value.isValid()) {
      return errorInvalidText;
    }

    return undefined;
  };
};
getNotEmptyValidator();

var getNotEmptyValidator$1 = function getNotEmptyValidator() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$errorText = _ref.errorText,
      errorText = _ref$errorText === void 0 ? 'Das Feld muss ausgefüllt werden' : _ref$errorText;

  return function (value) {
    return value ? undefined : errorText;
  };
};
var notEmptyValidator = getNotEmptyValidator$1();

var notEmptyAndIsEmailValidator = function notEmptyAndIsEmailValidator(value) {
  if (notEmptyValidator(value)) {
    return notEmptyValidator(value);
  }

  return emailValidator(value);
};

var getRepeatPasswordValidator = function getRepeatPasswordValidator() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$passwordPropsNam = _ref.passwordPropsName,
      passwordPropsName = _ref$passwordPropsNam === void 0 ? 'password' : _ref$passwordPropsNam,
      _ref$errorText = _ref.errorText,
      errorText = _ref$errorText === void 0 ? 'Die eingegebenen Passwörter stimmen nicht überein' : _ref$errorText;

  return function (value, allValues) {
    return value && (value === allValues[passwordPropsName] ? undefined : errorText);
  };
};
getRepeatPasswordValidator();

var id = 0;
var createActionWithCustomPayload = function createActionWithCustomPayload(createPayload, description) {
  id += 1;
  var type = "".concat(description || 'DEFAULT_ACTION', "_").concat(id);

  var actionCreator = function actionCreator() {
    return {
      type: type,
      payload: createPayload.apply(void 0, arguments)
    };
  };

  actionCreator.toString = function () {
    return type;
  };

  return actionCreator;
};

var defaultPayloadCreator = function defaultPayloadCreator(payload) {
  return payload;
};

var createAction = function createAction(description) {
  return createActionWithCustomPayload(defaultPayloadCreator, description);
};

var createReducer = function createReducer(initialState, handlers) {
  return function () {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
    var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return Object.prototype.hasOwnProperty.call(handlers, action.type) ? handlers[action.type](state, action) : state;
  };
};

var simpleMemoize = function simpleMemoize(fn) {
  var cache = {};
  return function (val) {
    if (!cache[val]) {
      cache[val] = fn(val);
    }

    return cache[val];
  };
};

var idCounter = 0;
var baseIdCounter = 0;
var baseId = '0';

var uniquify = function uniquify(name) {
  idCounter += 1;

  if (idCounter > 1000000) {
    baseIdCounter += 1;
    baseId = "".concat(baseId, "_").concat(baseIdCounter, "_");
    idCounter = 0;
  }

  return "".concat(name, "_").concat(baseId, "_").concat(idCounter);
};

var firstOrFalse = function firstOrFalse() {
  for (var _len = arguments.length, validators = new Array(_len), _key = 0; _key < _len; _key++) {
    validators[_key] = arguments[_key];
  }

  return function (value) {
    return validators.reduce(function (error, validate) {
      return error || validate(value);
    }, false);
  };
};
var timeoutPromise$1 = function timeoutPromise(timeoutTimeInMs, val) {
  return new Promise(function (resolve) {
    return setTimeout(function () {
      return resolve(val);
    }, timeoutTimeInMs);
  });
};
var throwF = function throwF(errorMessage) {
  throw new Error(errorMessage);
};
var omitProps = function omitProps(keys) {
  return mapProps(function (props) {
    return omit(keys, props);
  });
};

var globalNavigator = typeof navigator !== 'undefined' && navigator;
var languageFromNavigator = globalNavigator && (globalNavigator.userLanguage || navigator.language);
var language = languageFromNavigator || '';
var clearUpLanguageCode = compose$1(head, split('-'));
var getBrowserLocale = clearUpLanguageCode.bind(null, language);
getBrowserLocale();

var log = function log() {
  var _console;

  return (_console = console).log.apply(_console, arguments);
};
var logError = function logError() {
  var _console2;

  return (_console2 = console).error.apply(_console2, arguments);
};
var chainLog = function chainLog(logFunc) {
  return tap(logFunc);
};

var windowLocalStorage = typeof window !== 'undefined' && window.localStorage;
var globalLocalStorage = typeof localStorage !== 'undefined' && localStorage;
var defaultStorage = {};

var setItem = function setItem(key, value) {
  defaultStorage[key] = value;
};

var getItem = function getItem(key) {
  return defaultStorage[key];
};

var exportableLocalStorage = windowLocalStorage || globalLocalStorage || {
  setItem: setItem,
  getItem: getItem
};

export { checkBrowserForIos, isIos, toError, simpleMemoize, mergeTwoArraysUniqForProp, checkGlobalVariable, loadFromProps, withLoadingHandler, headIfExists, mergeTwoArraysUniq, pick, concatAll, pipeX, uniqueArrayConcat, isAny, getPrevMemoizedFunc, isFunction, getErrorFromProp, uniquify, omitProps, throwF, timeoutPromise$1 as timeoutPromise, firstOrFalse, switchF, base64ToBlob, fileToDataUri, withHandler, withProp, withComputedProp, OnDidMountOrUpdate, OnDidMount, formikNotEmptyValidator, formikEmailValidator, differentKeys, duplicateKeys, withPropOnChange, removeFalsyProps, objectFromKeys, mergeArrayProp, isObject, tryParseJson, pIsNaN, pTap, objectToArray, findInObject, parseFloatOrDefault, parseIntOrDefault, computeDistanceOnEarth, handleAxiosError, onPropsDidUpdate, onMountOrUpdate, onMount, onWillUnmount, createContainer, isNotNil, propIsNil, withCustomErrorProp, oneOfPropHasChanged, getComponentDisplayName, renderIfHasProp, renderIfHasError, renderIf, createActionWithCustomPayload, createAction, createReducer, getEmailValidator, getMinLengthValidator, getNotEmptyValidator, notEmptyAndIsEmailValidator, getRepeatPasswordValidator, language, getBrowserLocale, promiseWithError, logError, log, chainLog, exportableLocalStorage as pLocalStorage, propIsNotNil, promiseGeocode, geocodeGermanAddressWithGoogleGeocoder, AUTO_COMPLETE_REQUEST_OPTIONS, germanComponentRestrictions, is404, is408, connectionAborted, first };
//# sourceMappingURL=apoly-utils.esm.js.map
